import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import * as MarketingActions from '../../state/marketing/marketing.actions';
import * as MarketingSelectors from '../../state/marketing/marketing.selectors';
import { LeadMagnetResponse } from '../../services/marketing.service';

interface Testimonial {
  quote: string;
  name: string;
  title: string;
  company: string;
  avatar?: string;
}

@Component({
  selector: 'app-market-intelligence-report',
  templateUrl: './market-intelligence-report.component.html',
  styleUrls: ['./market-intelligence-report.component.scss']
})
export class MarketIntelligenceReportComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  openFAQs: boolean[] = [false, false, false, false, false, false];
  currentTestimonialIndex: number = 0;

  leadMagnetForm!: FormGroup;
  loading$: Observable<boolean>;
  error$: Observable<any>;
  response$: Observable<LeadMagnetResponse | null>;

  testimonials: Testimonial[] = [
    {
      quote: "This market intelligence report gave us the clarity we needed to focus our sales efforts. We identified 3x more qualified prospects in our target market.",
      name: "Sarah Johnson",
      title: "VP of Sales",
      company: "TechFlow Solutions"
    },
    {
      quote: "The data was incredibly accurate and actionable. We used the insights to refine our messaging and saw a 40% increase in response rates.",
      name: "Michael Chen",
      title: "Founder & CEO",
      company: "DataDrive Analytics"
    },
    {
      quote: "Finally, a report that's actually useful! The competitor analysis and market positioning insights helped us pivot our strategy and land bigger deals.",
      name: "Emily Rodriguez",
      title: "Head of Growth",
      company: "ScaleUp Ventures"
    }
  ];

  constructor(
    private fb: FormBuilder,
    private store: Store
  ) {
    this.loading$ = this.store.select(MarketingSelectors.selectLeadMagnetLoading);
    this.error$ = this.store.select(MarketingSelectors.selectLeadMagnetError);
    this.response$ = this.store.select(MarketingSelectors.selectLeadMagnetResponse);
  }

  ngOnInit(): void {
    this.initializeForm();
    this.subscribeToSuccess();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.leadMagnetForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      linkedin_url: ['', [Validators.required, Validators.pattern(/^https:\/\/(www\.)?linkedin\.com\/.+/)]]
    });
  }

  private subscribeToSuccess(): void {
    this.response$
      .pipe(
        takeUntil(this.destroy$),
        filter(response => response !== null)
      )
      .subscribe(response => {
        console.log('Lead magnet registration successful:', response);
        // Reset form on success
        this.leadMagnetForm.reset();
        // You can add navigation or success notification here
        // For example: this.router.navigate(['/dashboard']);
      });

    // Subscribe to errors for debugging
    this.error$
      .pipe(
        takeUntil(this.destroy$),
        filter(error => error !== null)
      )
      .subscribe(error => {
        console.error('Lead magnet registration error:', error);
      });
  }

  onSubmit(): void {
    if (this.leadMagnetForm.valid) {
      const formValue = this.leadMagnetForm.value;

      // Clear any previous errors or success messages before submitting
      this.store.dispatch(MarketingActions.clearLeadMagnetState());

      // Dispatch the registration action
      this.store.dispatch(MarketingActions.registerLeadMagnet({
        request: {
          email: formValue.email,
          linkedin_url: formValue.linkedin_url,
          lead_magnet_id: 'market-intelligence-report',
          utm_source: 'website',
          utm_campaign: 'market-intelligence-report',
          utm_medium: 'landing-page'
        }
      }));
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.leadMagnetForm.controls).forEach(key => {
        this.leadMagnetForm.get(key)?.markAsTouched();
      });
    }
  }

  toggleFAQ(index: number): void {
    this.openFAQs[index] = !this.openFAQs[index];
  }

  selectTestimonial(index: number): void {
    this.currentTestimonialIndex = index;
  }

  get currentTestimonial(): Testimonial {
    return this.testimonials[this.currentTestimonialIndex];
  }

}